import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../css/legal.css";

const PrivacyPage = () => {
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main className="legal-page">
        <h1>Privacy Policy</h1>
        <p className="legal-meta">
          Effective Date: July 28, 2026 &nbsp;|&nbsp; Last Updated: July 28, 2026
        </p>

        <p>
          This Privacy Policy describes how Continental Lease Advisors LLC
          ("CLA," "we," "us," or "our") collects, uses, and shares information
          through this website (continentalleasadvisors.com). It also describes
          the rights you may have under applicable U.S. state privacy laws.
        </p>

        <h2>1. Information We Collect</h2>
        <h3>Information you provide</h3>
        <p>
          When you submit the contact form on this site, we collect:
        </p>
        <ul>
          <li>Full name</li>
          <li>Company name</li>
          <li>Work email address</li>
          <li>Message content</li>
        </ul>
        <h3>Automatically collected information</h3>
        <p>
          When you visit this site, standard browser and server data is
          exchanged (e.g., IP address, browser type, referring URL, pages
          visited, time of visit). This data is used solely to operate the site
          and is not used to profile or track individual users. We do not
          operate our own analytics on this site.
        </p>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To respond to your inquiry submitted through the contact form</li>
          <li>To discuss or follow up on consulting services you request</li>
          <li>
            To comply with our legal obligations and enforce our Terms &amp;
            Conditions
          </li>
        </ul>
        <p>
          We do not use your information for advertising, behavioral profiling,
          or any purpose unrelated to your inquiry.
        </p>

        <h2>3. Third-Party Service Providers</h2>
        <p>
          We share limited information with the following service providers
          solely to operate this site:
        </p>
        <ul>
          <li>
            <strong>EmailJS</strong> — Processes and delivers contact form
            submissions. Your form data (name, company, email, and message) is
            transmitted to EmailJS servers to deliver your inquiry to us.
          </li>
          <li>
            <strong>Google Fonts</strong> — Loads typefaces from Google's
            content delivery network. Google may receive your IP address as part
            of font delivery.
          </li>
        </ul>
        <p>
          We do not sell, rent, or share your personal information with any
          third party for their own marketing or advertising purposes.
        </p>

        <h2>4. Cookies and Tracking</h2>
        <p>
          This site uses only essential functional cookies necessary for basic
          site operation. We do not use advertising, analytics, or
          cross-site tracking cookies. You may manage or delete cookies through
          your browser settings at any time without affecting your ability to
          use this site.
        </p>
        <p>
          We honor the Global Privacy Control (GPC) signal. If your browser
          sends a GPC signal, we automatically apply an opt-out of any
          non-essential data sharing.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          Contact form submissions are retained for as long as reasonably
          necessary to respond to your inquiry and for related business
          recordkeeping, after which they are deleted. We do not retain personal
          information beyond what is needed for the stated purpose.
        </p>

        <h2>6. Security</h2>
        <p>
          We implement reasonable technical and organizational measures to
          protect information against unauthorized access, disclosure, or loss.
          No transmission method over the Internet is completely secure, and we
          cannot guarantee absolute security.
        </p>

        <h2>7. Children's Privacy</h2>
        <p>
          This site is not directed at individuals under the age of 13. We do
          not knowingly collect personal information from children. If you
          believe a child has submitted information through this site, contact
          us at [CONTACT EMAIL] and we will delete it promptly.
        </p>

        <h2>8. Your Privacy Rights</h2>
        <p>
          Depending on the state where you reside, you may have the following
          rights regarding your personal information. To exercise any of these
          rights, see Section 9 below.
        </p>

        <h3>California Residents (CCPA / CPRA)</h3>
        <p>
          Under the California Consumer Privacy Act (CCPA) and California
          Privacy Rights Act (CPRA), California residents have the right to:
        </p>
        <ul>
          <li>
            <strong>Know</strong> — Request disclosure of the categories and
            specific pieces of personal information we have collected about you.
          </li>
          <li>
            <strong>Delete</strong> — Request deletion of your personal
            information, subject to certain exceptions.
          </li>
          <li>
            <strong>Correct</strong> — Request correction of inaccurate personal
            information we hold about you.
          </li>
          <li>
            <strong>Opt Out of Sale or Sharing</strong> — We do not sell or
            share personal information for cross-context behavioral advertising.
            You may still submit an opt-out request via the "Do Not Sell or
            Share My Personal Information" link in our site footer.
          </li>
          <li>
            <strong>Non-Discrimination</strong> — We will not discriminate
            against you for exercising any of your CCPA/CPRA rights.
          </li>
          <li>
            <strong>Limit Use of Sensitive Personal Information</strong> — We do
            not collect sensitive personal information as defined under CPRA.
          </li>
        </ul>
        <p>
          We will respond to verifiable consumer requests within 45 calendar
          days, with a possible 45-day extension where reasonably necessary.
        </p>

        <h3>Virginia Residents (VCDPA)</h3>
        <p>
          Under the Virginia Consumer Data Protection Act (VCDPA), Virginia
          residents have the right to access, correct, delete, and obtain a
          portable copy of their personal data, and to opt out of the processing
          of personal data for purposes of targeted advertising, sale, or
          profiling. We do not engage in any of those activities. To submit a
          request, see Section 9.
        </p>

        <h3>Colorado Residents (CPA)</h3>
        <p>
          Under the Colorado Privacy Act (CPA), Colorado residents have the
          right to access, correct, delete, and obtain a copy of their personal
          data, and to opt out of the sale of personal data, targeted
          advertising, and profiling. We do not sell personal data or engage in
          targeted advertising or profiling. To submit a request, see Section 9.
        </p>

        <h3>Connecticut Residents (CTDPA)</h3>
        <p>
          Under the Connecticut Data Privacy Act (CTDPA), Connecticut residents
          have the right to access, correct, delete, and obtain a portable copy
          of their personal data, and to opt out of targeted advertising, sale
          of personal data, and certain types of profiling. We do not engage in
          those activities. To submit a request, see Section 9.
        </p>

        <h2>9. How to Exercise Your Rights</h2>
        <p>
          To submit a privacy request (access, deletion, correction, or
          opt-out), contact us by email at{" "}
          <a href="mailto:[CONTACT EMAIL]">[CONTACT EMAIL]</a> with the subject
          line "Privacy Request." Please include your name and the state where
          you reside. We will verify your identity before processing your
          request and respond within the timeframe required by applicable law
          (typically 45 days).
        </p>
        <p>
          You may also authorize an agent to submit a request on your behalf. We
          may require written authorization or proof of the agent's authority.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will update the "Last Updated" date at the top of this page. Continued
          use of the site after changes constitutes your acceptance of the
          revised policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          Continental Lease Advisors LLC<br />
          [COMPANY ADDRESS]<br />
          Chicago, IL<br />
          Email: <a href="mailto:[CONTACT EMAIL]">[CONTACT EMAIL]</a>
        </p>

        <div className="legal-placeholders">
          <p>Placeholders to fill in before publishing:</p>
          <ul>
            <li>[CONTACT EMAIL] — privacy/legal contact email address</li>
            <li>[COMPANY ADDRESS] — CLA's street address</li>
          </ul>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPage;